import { composeRefs } from '@radix-ui/react-compose-refs';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { type DropzoneRootProps } from 'react-dropzone';

import { fileDropSurfaceRootVariants } from './file-drop-surface.styles';
import { FileDropTakeover } from './file-drop-takeover';
import { type FileDropSurfaceProps } from './file-upload.types';
import { useFileUploadDropzone } from './use-file-upload-dropzone';

const dragHasFiles = (event: DragEvent): boolean =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

function resolveDropContainer(
  container: FileDropSurfaceProps['container']
): HTMLElement | null {
  if (container == null) return null;
  // SSR has no `HTMLElement` global. `instanceof` throws when the right-hand
  // side is undefined, and container-scope callers pass a ref.
  if (typeof HTMLElement !== 'undefined' && container instanceof HTMLElement) {
    return container;
  }
  return 'current' in container ? container.current : null;
}

const pointerInside = (event: DragEvent, element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
};

/**
 * react-dropzone drops the files if propagation was stopped by the time its
 * file read resolves. The window drop listener and this handler both stop the
 * native event, so the dropzone gets a stand-in whose flag stays clear.
 * `dataTransfer` is the real one — it is only readable during this call.
 */
const asDropzoneEvent = (event: DragEvent): DragEvent => {
  const dropEvent = {
    type: event.type,
    target: event.target,
    currentTarget: event.currentTarget,
    dataTransfer: event.dataTransfer,
    preventDefault() {
      event.preventDefault();
    },
    stopPropagation() {},
    persist() {},
    isPropagationStopped: () => false,
    get cancelBubble() {
      return false;
    },
  };
  return dropEvent as unknown as DragEvent;
};

/**
 * True while a file drag is inside `container`. The overlay is visual only.
 *
 * The drop is read off `container`, not the overlay: mounting the overlay under
 * the pointer fires dragleave, which can unmount it before mouseup.
 *
 * Capture-phase listeners stop propagation, so a nested `FileDropzone` never
 * gets the drop.
 */
function useScopedFileDrag(
  container: HTMLElement | null,
  enabled: boolean,
  onDropRef: React.RefObject<((event: DragEvent) => void) | null>
): boolean {
  const [isOver, setIsOver] = React.useState(false);

  React.useEffect(() => {
    if (!enabled || !container) return;

    const reset = () => {
      setIsOver(false);
    };
    const handleDragEnter = (event: DragEvent) => {
      if (dragHasFiles(event)) setIsOver(true);
    };
    const handleDragOver = (event: DragEvent) => {
      if (!dragHasFiles(event)) return;
      event.preventDefault();
      setIsOver(true);
    };
    const handleDragLeave = (event: DragEvent) => {
      if (pointerInside(event, container)) return;
      reset();
    };
    const handleDrop = (event: DragEvent) => {
      if (dragHasFiles(event)) {
        event.preventDefault();
        event.stopPropagation();
        onDropRef.current?.(asDropzoneEvent(event));
      }
      queueMicrotask(reset);
    };

    container.addEventListener('dragenter', handleDragEnter, true);
    container.addEventListener('dragover', handleDragOver, true);
    container.addEventListener('dragleave', handleDragLeave, true);
    container.addEventListener('drop', handleDrop, true);
    document.addEventListener('dragend', reset);

    return () => {
      container.removeEventListener('dragenter', handleDragEnter, true);
      container.removeEventListener('dragover', handleDragOver, true);
      container.removeEventListener('dragleave', handleDragLeave, true);
      container.removeEventListener('drop', handleDrop, true);
      document.removeEventListener('dragend', reset);
      reset();
    };
  }, [container, enabled, onDropRef]);

  return isOver;
}

function useDropContainer(
  container: FileDropSurfaceProps['container']
): HTMLElement | null {
  const live = resolveDropContainer(container);
  const [node, setNode] = React.useState(live);

  // The boundary ref is often still null during render: the host attaches it
  // in the commit, before passive effects. Re-read it here so the listener
  // binds on the next render without the ref identity having to change.
  React.useEffect(() => {
    setNode(resolveDropContainer(container));
  }, [container]);

  return live ?? node;
}

/**
 * Viewport or container file drop. Validates through the same react-dropzone
 * contract as `FileDropzone` and only reports files that passed it.
 *
 * `scope="window"` is always the viewport. A drop that should fill some other
 * region — the app content column, a docked panel — uses `scope="container"`
 * and the caller passes that element.
 *
 * Takes every file drop in its boundary; see `FileDropSurfaceProps`.
 */
const FileDropSurface = React.forwardRef<HTMLDivElement, FileDropSurfaceProps>(
  (
    {
      'data-testid': testId = 'file-drop-surface',
      scope,
      container,
      onFilesAccepted,
      onFilesRejected,
      accept,
      multiple = false,
      maxFiles = 0,
      maxSize,
      minSize,
      disabled = false,
      label,
      normalizeFile,
    },
    ref
  ) => {
    const { getRootProps } = useFileUploadDropzone({
      accept,
      multiple,
      maxFiles,
      maxSize,
      minSize,
      disabled,
      onFilesAccepted,
      onFilesRejected,
      method: 'drag_drop',
      normalizeFile,
      noClick: true,
      noKeyboard: true,
    });
    const onDropRef = React.useRef<((event: DragEvent) => void) | null>(null);
    const { ref: dropzoneRef, onDrop, ...rootProps } = getRootProps<DropzoneRootProps>();
    onDropRef.current = onDrop
      ? (event) => {
          onDrop(event as unknown as React.DragEvent<HTMLElement>);
        }
      : null;

    const viewport =
      scope === 'window' && typeof document !== 'undefined' ? document.body : null;
    const containerNode = useDropContainer(scope === 'container' ? container : null);
    const boundsElement = scope === 'window' ? viewport : containerNode;
    const isOverBounds = useScopedFileDrag(
      boundsElement,
      boundsElement != null && !disabled,
      onDropRef
    );

    const active = !disabled && isOverBounds;
    const portalTarget =
      scope === 'window'
        ? typeof document !== 'undefined'
          ? document.body
          : null
        : containerNode;

    if (!active || portalTarget == null) return null;

    return createPortal(
      <div
        {...rootProps}
        onDrop={undefined}
        ref={composeRefs(ref, dropzoneRef as React.Ref<HTMLDivElement>)}
        data-testid={testId}
        className={fileDropSurfaceRootVariants({ scope })}
      >
        <FileDropTakeover inline visible scope={scope} label={label} />
      </div>,
      portalTarget
    );
  }
);
FileDropSurface.displayName = 'FileDropSurface';

export { FileDropSurface };
